import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { Stack } from '@startuptools/ui/Stack';
import { DateTime } from 'luxon';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { type DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { useState } from 'react';
import { type GqlCreateIdentityInput, type GqlTodo } from '../../../graphql/base-types.graphql';
import { useCreateIdentityMutation } from '../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { type DocumentNode } from 'graphql';
import { useApolloClient } from '@apollo/client';
import { DateInput } from '@startuptools/ui/DateInput';
import { SelectSingleIdentity } from '@startuptools/business-ui/identity/select/SelectSingleIdentity';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { JuridicalKind } from '../../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';

export type FormValues = {
  id?: string;
  descriptionPrimaryLang: string;
  descriptionSecondaryLang: string;
  dueDate: DateTime | null | undefined;
  identity: DisplayIdentity | null;
};

export type EditableTodo = Pick<
  GqlTodo,
  'descriptionPrimaryLang' | 'descriptionSecondaryLang' | 'dueDate' | 'id' | 'identityId'
> & {
  identity?: DisplayIdentity | null;
};

export const TodoForm = ({
  refetchQueries,
  onCancel,
  onSubmit,
  todo,
}: {
  refetchQueries: (DocumentNode | string)[];
  onCancel: () => void;
  onSubmit: SubmitHandler<FormValues>;
  todo?: EditableTodo;
}) => {
  const companyId = useCompanyId();
  const apollo = useApolloClient();

  const [showAddIdentity, setShowAddIdentity] = useState(false);
  const { control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      id: todo?.id,
      identity: todo?.identity,
      descriptionPrimaryLang: todo?.descriptionPrimaryLang ?? '',
      descriptionSecondaryLang: todo?.descriptionSecondaryLang ?? '',
      dueDate: todo?.dueDate ? DateTime.fromISO(todo.dueDate) : null,
    },
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateIdenity] = useCreateIdentityMutation();
  const handleCreateIdentity = async (identity: GqlCreateIdentityInput) => {
    const res = await doCreateIdenity({ variables: { companyId, input: identityToInput(identity) } });
    if (res.data) {
      await apollo.refetchQueries({ include: refetchQueries });
      setValue('identity', res.data.createIdentity);
      setShowAddIdentity(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Controller
            control={control}
            name="descriptionPrimaryLang"
            render={({ field }) => (
              <FormControl required>
                <FormLabel>
                  <Trans id="Description" />
                </FormLabel>
                <Input {...field} />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="dueDate"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Due Date" />
                </FormLabel>
                <DateInput
                  {...field}
                  slotProps={{ field: { clearable: true } }}
                  onChange={newValue => field.onChange(newValue)}
                />
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="identity"
            render={({ field: { onChange, ...field } }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Assignee" />
                </FormLabel>
                <SelectSingleIdentity
                  {...field}
                  placeholder="Choose a signee"
                  onChange={(_, identity) => onChange(identity)}
                  selectIdentity={() => setShowAddIdentity(true)}
                />
              </FormControl>
            )}
          />
          <Stack direction="row" gap={1} justifyContent="end">
            <Button variant="outlined" color="neutral" onClick={onCancel}>
              <Trans id="Cancel" />
            </Button>
            <Button type="submit">
              <Trans id="Save" />
            </Button>
          </Stack>
        </Stack>
      </form>
      {showAddIdentity && (
        <AddIdentity
          headline={<Trans id="Add owner" />}
          juridicalKinds={[JuridicalKind.Person]}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.CompanyBoard]: false,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          defaultInputState={InputState.System}
          detailFieldsSettings={{}}
          handleCreate={handleCreateIdentity}
          onClose={() => setShowAddIdentity(false)}
        />
      )}
    </>
  );
};
